import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/roles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { ExportMenu } from "@/components/ExportMenu";
import { ToolBar } from "@/components/ToolBar";
import { LineItemsEditor, computeLineTotal } from "@/components/LineItemsEditor";
import { Plus, CreditCard, Coins, Warning, Receipt, DownloadSimple, UploadSimple, PencilSimple, ArrowUUpLeft, GitBranch } from "@phosphor-icons/react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { fmtCurrency, currencySymbol } from "@/lib/currency";
import { ArchiveButton } from "@/components/ArchiveButton";

const fmt = (n, cur) => fmtCurrency(n, cur || "PHP");

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
];

const EMPTY = { client_id: "", client_name: "", currency: "PHP", issue_date: "", due_date: "", items: [], vat_amount: 0, vat_status: "vatable", description: "", notes: "", reference: "", is_offset: false, offset_reason: "" };

const VAT_STATUS_OPTIONS = [
  { value: "vatable", label: "Vatable (12%)" },
  { value: "zero_rated", label: "Zero-Rated (0%)" },
  { value: "exempt", label: "VAT-Exempt" },
];

function KpiTile({ label, value, hint, tone = "default", icon: Icon, testId }) {
  const toneCls = {
    default: "bg-card",
    dark: "bg-foreground text-background",
    warn: "bg-rose-50 border-rose-200 text-rose-900",
  }[tone] || "bg-card";
  return (
    <div className={`border border-border p-5 ${toneCls}`} data-testid={testId}>
      <div className="flex items-start justify-between mb-3">
        <span className={`text-[10px] uppercase tracking-widest font-medium ${tone === "dark" ? "text-background/60" : tone === "warn" ? "opacity-70" : "text-muted-foreground"}`}>{label}</span>
        {Icon && <Icon size={16} className={tone === "dark" ? "text-background/70" : ""} />}
      </div>
      <div className="font-heading font-black tabular-nums text-2xl sm:text-3xl leading-none tracking-tight">{value}</div>
      {hint && <div className={`text-xs mt-2 ${tone === "dark" ? "text-background/60" : "opacity-70"}`}>{hint}</div>}
    </div>
  );
}

export default function ReceivablesPage() {
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  // "active" hides offset invoices; "offsets" only shows offsets.
  const [view, setView] = useState("active");
  // Offset action dialog for existing invoices.
  const [offsetOpen, setOffsetOpen] = useState(false);
  const [offsetTarget, setOffsetTarget] = useState(null);
  const [offsetReason, setOffsetReason] = useState("");
  // Track whether the VAT field is currently auto-driven (Subtotal × 12%).
  // Turned off the moment Finance types a manual override, or when opening
  // an existing invoice whose stored VAT doesn't match the auto value.
  const [vatAutoMode, setVatAutoMode] = useState(true);

  // Auto-compute VAT at 12% of the current subtotal whenever line items change
  // or the vat_status flips to "vatable" — provided we're still in auto mode.
  useEffect(() => {
    if (!vatAutoMode) return;
    if (form.vat_status !== "vatable") return;
    const subtotal = computeLineTotal(form.items);
    const auto = Math.round(subtotal * 0.12 * 100) / 100;
    setForm((prev) => (Number(prev.vat_amount) === auto ? prev : { ...prev, vat_amount: auto }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.items, form.vat_status, vatAutoMode]);

  const [payOpen, setPayOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payVat, setPayVat] = useState("");
  const [payEwtRate, setPayEwtRate] = useState("0");
  const [payMethod, setPayMethod] = useState("");
  const [payNote, setPayNote] = useState("");

  const canCreate = can(user, ["admin", "finance"]);
  const canPay = can(user, ["admin", "finance"]);
  const canDelete = can(user, ["admin"]);
  const canTransfer = can(user, ["admin", "finance"]);

  async function handleExport() {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/receivables/export`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `receivables_export_${stamp}.json`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success("Receivables exported");
    } catch (err) { toast.error("Export failed"); }
  }

  async function handleImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const text = await file.text();
      const payload = JSON.parse(text);
      const { data } = await api.post("/receivables/import", { payload, mode: "upsert" });
      const ins = data.inserted, upd = data.updated;
      toast.success(`Imported: +${ins.invoices} invoices / +${ins.clients} clients (updated ${upd.invoices}/${upd.clients})`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed — is this a receivables_export JSON?");
    }
  }

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (status) params.payment_status = status;
      if (showArchived) params.include_archived = true;
      const [i, c, s] = await Promise.all([
        api.get("/client-invoices", { params }),
        api.get("/clients"),
        api.get("/receivables/summary"),
      ]);
      setInvoices(i.data);
      setClients(c.data);
      setSummary(s.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, showArchived]);

  const filtered = useMemo(() => invoices.filter((inv) => {
    // View tab filter — Active hides offsets, Offsets only shows offsets.
    if (view === "active" && inv.is_offset) return false;
    if (view === "offsets" && !inv.is_offset) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      inv.invoice_no?.toLowerCase().includes(q) ||
      inv.client_name?.toLowerCase().includes(q) ||
      inv.description?.toLowerCase().includes(q) ||
      inv.reference?.toLowerCase().includes(q)
    );
  }), [invoices, search, view]);

  const offsetCount = useMemo(() => invoices.filter((i) => i.is_offset).length, [invoices]);

  async function submit(e) {
    e.preventDefault();
    if (!form.client_id) { toast.error("Please select a client"); return; }
    const subtotal = computeLineTotal(form.items);
    const vat = form.vat_status === "vatable" ? (Number(form.vat_amount) || 0) : 0;
    const payload = {
      ...form,
      vat_amount: vat,
      total_amount: subtotal + vat,
      is_offset: !!form.is_offset,
      offset_reason: form.is_offset ? (form.offset_reason || "").trim() : "",
    };
    try {
      if (editing) {
        await api.put(`/client-invoices/${editing.id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/client-invoices", payload);
        toast.success(payload.is_offset ? "Offset invoice recorded" : "Invoice created");
      }
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  }

  function openEdit(inv) {
    setEditing(inv);
    const items = inv.items || [];
    const subtotal = computeLineTotal(items);
    const storedVat = Number(inv.vat_amount) || 0;
    const auto12 = Math.round(subtotal * 0.12 * 100) / 100;
    // Preserve manual overrides on edit — only leave auto-mode ON when the
    // stored VAT still matches Subtotal × 12% (± 0.01 for float safety).
    setVatAutoMode(
      (inv.vat_status || "vatable") === "vatable" && Math.abs(storedVat - auto12) < 0.01,
    );
    setForm({
      client_id: inv.client_id || "",
      client_name: inv.client_name || "",
      currency: inv.currency || "PHP",
      issue_date: inv.issue_date || "",
      due_date: inv.due_date || "",
      items,
      vat_amount: storedVat,
      vat_status: inv.vat_status || "vatable",
      description: inv.description || "",
      notes: inv.notes || "",
      reference: inv.reference || "",
      is_offset: !!inv.is_offset,
      offset_reason: inv.offset_reason || "",
    });
    setOpen(true);
  }

  function openOffset(inv) {
    setOffsetTarget(inv);
    setOffsetReason(inv.offset_reason || "");
    setOffsetOpen(true);
  }

  async function submitOffset(e) {
    e.preventDefault();
    if (!offsetTarget) return;
    try {
      await api.post(`/client-invoices/${offsetTarget.id}/offset`, { reason: offsetReason.trim() });
      toast.success(`${offsetTarget.invoice_no} marked as offset`);
      setOffsetOpen(false);
      setOffsetTarget(null);
      setOffsetReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to mark offset");
    }
  }

  async function reverseOffset(inv) {
    if (!window.confirm(`Reverse the offset on ${inv.invoice_no}? It will re-enter Accounts Receivable with its remaining balance.`)) return;
    try {
      await api.post(`/client-invoices/${inv.id}/unoffset`);
      toast.success(`${inv.invoice_no} restored to receivables`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reverse offset");
    }
  }

  function openPayment(inv) {
    setPayTarget(inv);
    setPayEwtRate("0");
    setPayMethod("");
    setPayNote("");
    setPayOpen(true);
  }
  async function submitPayment(e) {
    e.preventDefault();
    const invoiceAmt = Number(payTarget?.balance) || 0;   // settle current balance
    const invoiceVat = Number(payTarget?.vat_amount) || 0;
    const invoiceTotal = Number(payTarget?.total_amount) || 0;
    const vatable = Math.max(0, invoiceTotal - invoiceVat);
    const rate = Number(payEwtRate) || 0;
    const ewt = Math.round(vatable * (rate / 100) * 100) / 100;
    // Proportional VAT portion of the balance being settled (usually the full
    // invoice VAT when balance == total).
    const vatPortion = invoiceTotal > 0 ? Math.round((invoiceVat * (invoiceAmt / invoiceTotal)) * 100) / 100 : 0;
    try {
      await api.post(`/client-invoices/${payTarget.id}/payment`, {
        amount: invoiceAmt,
        vat_portion: vatPortion,
        ewt_rate: rate,
        ewt_amount: ewt,
        method: payMethod || undefined,
        note: payNote || undefined,
      });
      toast.success("Payment recorded");
      setPayOpen(false);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  }

  async function removeInvoice(inv) {
    try {
      await api.delete(`/client-invoices/${inv.id}`);
      toast.success(`Invoice ${inv.invoice_no} archived`);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Archive failed"); }
  }

  return (
    <div className="space-y-6" data-testid="receivables-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2">Accounts Receivable</div>
          <h1 className="section-title text-3xl sm:text-4xl">Client Billing & Collections</h1>
          <p className="text-sm text-muted-foreground mt-1">Invoices you've sent to clients — track what's still owed and record payments as they come in.</p>
        </div>
        {canCreate && (
          <div className="flex flex-wrap gap-2">
            {canTransfer && (
              <>
                <Button variant="outline" onClick={handleExport} className="border-border" data-testid="receivables-export-btn">
                  <DownloadSimple size={16} className="mr-2" /> Export JSON
                </Button>
                <label className="inline-flex" data-testid="receivables-import-label">
                  <input type="file" accept="application/json,.json" onChange={handleImportFile} className="hidden" data-testid="receivables-import-input" />
                  <span className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium cursor-pointer hover:bg-muted">
                    <UploadSimple size={16} /> Import JSON
                  </span>
                </label>
              </>
            )}
            <Button onClick={() => { setEditing(null); setForm(EMPTY); setVatAutoMode(true); setOpen(true); }} data-testid="new-invoice-btn">
              <Plus size={16} className="mr-2" /> New Invoice
            </Button>
          </div>
        )}
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiTile label="Outstanding" value={fmt(summary?.total_outstanding)} tone="dark" icon={Coins} testId="kpi-outstanding" />
        <KpiTile label="Overdue Invoices" value={summary?.overdue_count ?? 0} tone={summary?.overdue_count ? "warn" : "default"} icon={Warning} testId="kpi-overdue-count" hint={summary?.overdue_count ? "Past due date" : "Nothing overdue"} />
        <KpiTile label="Collected" value={fmt(summary?.total_paid)} icon={Receipt} testId="kpi-collected" />
        <KpiTile label="Total Invoiced" value={fmt((summary?.total_outstanding || 0) + (summary?.total_paid || 0))} icon={CreditCard} testId="kpi-total-invoiced" />
      </div>

      {/* Aging */}
      {summary?.aging && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="border border-border rounded-md bg-card">
          <div className="p-4 border-b border-border">
            <div className="section-caption">Aging</div>
            <div className="font-heading font-bold tracking-tight">Outstanding by bucket</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-border">
            {[
              ["Current", summary.aging.current, "text-emerald-700"],
              ["1–30 d", summary.aging.days_1_30, "text-amber-700"],
              ["31–60 d", summary.aging.days_31_60, "text-amber-700"],
              ["61–90 d", summary.aging.days_61_90, "text-rose-700"],
              ["90+ d", summary.aging.days_90_plus, "text-rose-700"],
            ].map(([label, v, cls]) => (
              <div key={label} className="p-4" data-testid={`aging-${label.replace(/[^0-9a-z]/gi, '-').toLowerCase()}`}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
                <div className={`font-heading font-bold tabular-nums text-lg ${cls}`}>{fmt(v)}</div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      <ToolBar
        search={search} onSearch={setSearch}
        status={status} onStatus={setStatus} statusOptions={STATUS_OPTIONS}
        placeholder="Search invoice no, client, reference…"
        showArchived={showArchived} onToggleArchived={setShowArchived}
      />

      {/* View tabs — Active hides offsets; Offsets isolates them for review */}
      <div className="flex items-center gap-1 border border-border rounded-md bg-card p-1 w-fit" data-testid="receivables-view-tabs">
        <button
          type="button"
          onClick={() => setView("active")}
          className={`px-3 py-1.5 text-xs font-medium rounded ${view === "active" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
          data-testid="view-tab-active"
        >
          Active
        </button>
        <button
          type="button"
          onClick={() => setView("offsets")}
          className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 ${view === "offsets" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
          data-testid="view-tab-offsets"
        >
          <GitBranch size={12} /> Offsets
          {offsetCount > 0 && (
            <span className={`text-[10px] tabular-nums px-1.5 rounded-full ${view === "offsets" ? "bg-primary-foreground/20" : "bg-muted"}`}>{offsetCount}</span>
          )}
        </button>
        <button
          type="button"
          onClick={() => setView("all")}
          className={`px-3 py-1.5 text-xs font-medium rounded ${view === "all" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
          data-testid="view-tab-all"
        >
          All
        </button>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[1000px]">
          <TableHeader>
            <TableRow>
              <TableHead>Invoice No.</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Issued</TableHead>
              <TableHead>Due</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="py-8 text-center text-sm text-muted-foreground">No invoices yet. Create one to start tracking collections.</TableCell></TableRow>
            ) : filtered.map((inv) => (
              <TableRow
                key={inv.id}
                data-testid={`invoice-row-${inv.id}`}
                className={inv.is_offset ? "bg-muted/40 text-muted-foreground" : undefined}
              >
                <TableCell className="font-mono text-xs">
                  <div className="flex items-center gap-2">
                    <span>{inv.invoice_no}</span>
                    {inv.is_offset && (
                      <span
                        className="text-[9px] font-bold uppercase tracking-widest bg-zinc-200 text-zinc-700 px-1.5 py-0.5 rounded"
                        title={inv.offset_reason || "Offset — non-cash settlement"}
                        data-testid={`offset-tag-${inv.id}`}
                      >
                        Offset
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Link to={`/clients/${inv.client_id}`} className="font-medium hover:underline">{inv.client_name}</Link>
                  {inv.reference && <div className="text-xs text-muted-foreground">Ref: {inv.reference}</div>}
                  {inv.is_offset && inv.offset_reason && (
                    <div className="text-[11px] italic text-muted-foreground mt-0.5" data-testid={`offset-reason-${inv.id}`}>
                      Offset · {inv.offset_reason}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-sm">{inv.issue_date || "—"}</TableCell>
                <TableCell className="text-sm">{inv.due_date || "—"}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(inv.total_amount, inv.currency)}</TableCell>
                <TableCell className={`text-right tabular-nums ${inv.is_offset ? "" : "text-emerald-700"}`}>{inv.is_offset ? "—" : fmt(inv.amount_paid, inv.currency)}</TableCell>
                <TableCell className="text-right tabular-nums font-heading font-bold">
                  {inv.is_offset ? <span className="text-muted-foreground italic text-xs font-normal">excluded</span> : fmt(inv.balance, inv.currency)}
                </TableCell>
                <TableCell><StatusBadge status={inv.payment_status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    {canPay && !inv.is_offset && inv.payment_status !== "paid" && inv.payment_status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="border-border" onClick={() => openPayment(inv)} data-testid={`record-payment-${inv.id}`}>
                        <CreditCard size={14} className="mr-1" /> Payment
                      </Button>
                    )}
                    {canPay && !inv.is_offset && inv.payment_status !== "paid" && inv.payment_status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="border-border" onClick={() => openOffset(inv)} data-testid={`mark-offset-${inv.id}`} title="Mark as Offset — excludes from receivables">
                        <GitBranch size={14} className="mr-1" /> Offset
                      </Button>
                    )}
                    {canPay && inv.is_offset && (
                      <Button size="sm" variant="outline" className="border-border" onClick={() => reverseOffset(inv)} data-testid={`reverse-offset-${inv.id}`} title="Reverse the offset — invoice returns to AR">
                        <ArrowUUpLeft size={14} className="mr-1" /> Reverse
                      </Button>
                    )}
                    {canCreate && inv.payment_status !== "paid" && inv.payment_status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="border-border" onClick={() => openEdit(inv)} data-testid={`edit-invoice-${inv.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                    )}
                    <ExportMenu endpointPath={`/exports/client-invoice/${inv.id}`} baseName={`invoice_${inv.invoice_no}`} testId={`export-invoice-${inv.id}`} />
                    {canDelete && (
                      <ArchiveButton endpoint={`/client-invoices/${inv.id}`} label={inv.invoice_no} onArchived={load} testId={`archive-invoice-${inv.id}`} />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Create / edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(EMPTY); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-heading font-black">{editing ? `Edit ${editing.invoice_no}` : "New Invoice"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-[1fr_140px] gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Bill to Client</Label>
                <Select value={form.client_id} onValueChange={(v) => { const cl = clients.find((c) => c.id === v); setForm({ ...form, client_id: v, client_name: cl?.name || "" }); }}>
                  <SelectTrigger data-testid="invoice-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Currency</Label>
                <div className="h-10 px-3 flex items-center border border-border rounded bg-muted/30 tabular-nums text-sm" data-testid="invoice-currency-display">₱ PHP (Philippine Peso)</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Issue Date</Label>
                <Input type="date" value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} className="border-border" data-testid="invoice-issue-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="border-border" data-testid="invoice-due-date" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reference</Label>
                <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="PO / project" className="border-border" data-testid="invoice-reference" />
              </div>
            </div>
            <LineItemsEditor items={form.items} onChange={(it) => setForm({ ...form, items: it })} />
            <div className="p-3 border border-border rounded bg-muted/30 space-y-3" data-testid="invoice-totals-row">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-widest text-muted-foreground">VAT Status</Label>
                  <Select
                    value={form.vat_status}
                    onValueChange={(v) => {
                      // Switching back to Vatable re-enables auto-mode so the
                      // computed 12% VAT snaps in on the next render.
                      if (v === "vatable") setVatAutoMode(true);
                      setForm({ ...form, vat_status: v, vat_amount: v === "vatable" ? form.vat_amount : 0 });
                    }}
                  >
                    <SelectTrigger data-testid="invoice-vat-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VAT_STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground">Zero-rated and exempt sales are still reported to BIR (SLSP) but with 0 VAT.</p>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">VAT ({currencySymbol(form.currency)})</Label>
                    {form.vat_status === "vatable" && (
                      vatAutoMode ? (
                        <span className="text-[9px] uppercase tracking-widest text-emerald-700 dark:text-emerald-500" data-testid="invoice-vat-auto-badge">Auto · 12%</span>
                      ) : (
                        <button
                          type="button"
                          className="text-[9px] uppercase tracking-widest text-muted-foreground underline decoration-dotted hover:text-foreground"
                          onClick={() => setVatAutoMode(true)}
                          data-testid="invoice-vat-reset-auto-btn"
                        >
                          Reset to 12%
                        </button>
                      )
                    )}
                  </div>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    disabled={form.vat_status !== "vatable"}
                    value={form.vat_status === "vatable" ? form.vat_amount : 0}
                    onChange={(e) => { setVatAutoMode(false); setForm({ ...form, vat_amount: e.target.value }); }}
                    placeholder="0.00"
                    className="border-border tabular-nums font-heading disabled:opacity-60"
                    data-testid="invoice-vat-input"
                  />
                  <p className="text-[10px] text-muted-foreground">{form.vat_status === "vatable" ? (vatAutoMode ? "Auto-computed from Subtotal × 12%. Type to override." : "Manual override — click “Reset to 12%” to restore auto.") : "Locked at 0 for non-vatable sales."}</p>
                </div>
              </div>
              <div className="flex items-end justify-between border-t border-border pt-3">
                <div className="text-xs text-muted-foreground">Subtotal · <span className="tabular-nums text-foreground" data-testid="invoice-subtotal-display">{fmt(computeLineTotal(form.items), form.currency)}</span></div>
                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">Grand Total</div>
                  <div className="tabular-nums font-heading font-black text-lg" data-testid="invoice-grand-total-display">
                    {fmt(computeLineTotal(form.items) + (form.vat_status === "vatable" ? Number(form.vat_amount) || 0 : 0), form.currency)}
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Description</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="border-border" data-testid="invoice-description" />
            </div>

            {/* Offset — record but don't count against receivables */}
            <div className="p-3 border border-dashed border-border rounded bg-muted/20 space-y-2" data-testid="invoice-offset-block">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 accent-primary"
                  checked={!!form.is_offset}
                  onChange={(e) => setForm({ ...form, is_offset: e.target.checked })}
                  data-testid="invoice-offset-checkbox"
                />
                <div className="text-xs">
                  <div className="font-medium text-foreground">Offset — do not count as collectible</div>
                  <div className="text-muted-foreground mt-0.5">
                    Records the invoice for VAT / BIR purposes but excludes it from Outstanding, Aging, Dashboard AR, and the client's Statement of Account balance.
                  </div>
                </div>
              </label>
              {form.is_offset && (
                <Input
                  value={form.offset_reason}
                  onChange={(e) => setForm({ ...form, offset_reason: e.target.value })}
                  placeholder="Offset reason (e.g. Netted against supplier payable BR-2026-0123)"
                  className="border-border"
                  data-testid="invoice-offset-reason"
                />
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="invoice-submit-btn">{editing ? "Save changes" : "Create invoice"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment dialog — mirrors Casedist's collection flow: Invoice → */}
      {/* Vatable → VAT → EWT (1/2/5/7/10 %) → Net Collection. All values */}
      {/* auto-computed. Recording closes the invoice balance. */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="payment-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black">Record Payment — {payTarget?.invoice_no}</DialogTitle></DialogHeader>
          <form onSubmit={submitPayment} className="space-y-4">
            {(() => {
              const invoiceAmt = Number(payTarget?.balance) || 0;
              const invoiceTotal = Number(payTarget?.total_amount) || 0;
              const invoiceVat = Number(payTarget?.vat_amount) || 0;
              // Proportional split when only part of the invoice is being settled
              // (rare — most collections close the balance in one go).
              const propAmt = invoiceTotal > 0 ? invoiceAmt / invoiceTotal : 1;
              const vat = Math.round(invoiceVat * propAmt * 100) / 100;
              const vatable = Math.max(0, Math.round((invoiceAmt - vat) * 100) / 100);
              const rate = Number(payEwtRate) || 0;
              const ewt = Math.round(vatable * (rate / 100) * 100) / 100;
              const net = Math.max(0, Math.round((invoiceAmt - ewt) * 100) / 100);
              return (
                <>
                  <div className="p-3 border border-border rounded bg-muted/30 text-sm space-y-1.5">
                    <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="font-medium">{payTarget?.client_name}</span></div>
                    <div className="flex justify-between border-t border-border pt-1.5">
                      <span className="text-muted-foreground">Invoice Amount</span>
                      <span className="tabular-nums font-heading font-bold" data-testid="pay-invoice-amt">{fmt(invoiceAmt, payTarget?.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Vatable Amount</span>
                      <span className="tabular-nums" data-testid="pay-vatable-amt">{fmt(vatable, payTarget?.currency)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">VAT Amount ({invoiceTotal > 0 ? ((invoiceVat / invoiceTotal) * 100).toFixed(2) : "0.00"}%)</span>
                      <span className="tabular-nums text-amber-700" data-testid="pay-vat-amt">{fmt(vat, payTarget?.currency)}</span>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Less: EWT (Expanded Withholding Tax)</Label>
                    <Select value={payEwtRate} onValueChange={setPayEwtRate}>
                      <SelectTrigger data-testid="pay-ewt-select" className="border-border"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">None</SelectItem>
                        <SelectItem value="1">1% of Vatable Amount</SelectItem>
                        <SelectItem value="2">2% of Vatable Amount</SelectItem>
                        <SelectItem value="5">5% of Vatable Amount</SelectItem>
                        <SelectItem value="7">7% of Vatable Amount</SelectItem>
                        <SelectItem value="10">10% of Vatable Amount</SelectItem>
                      </SelectContent>
                    </Select>
                    {rate > 0 && (
                      <div className="flex justify-between text-xs pt-1">
                        <span className="text-muted-foreground">EWT ({rate}%) of Vatable</span>
                        <span className="tabular-nums text-rose-700" data-testid="pay-ewt-amt">− {fmt(ewt, payTarget?.currency)}</span>
                      </div>
                    )}
                  </div>

                  <div className="p-3 border-2 border-emerald-200 rounded bg-emerald-50/60 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-emerald-800">Net Collection</div>
                      <div className="text-[10px] text-emerald-700/80">Invoice Amount − EWT · Cash actually received</div>
                    </div>
                    <div className="tabular-nums font-heading font-black text-xl text-emerald-800" data-testid="pay-net-collection">
                      {fmt(net, payTarget?.currency)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Method</Label>
                      <Input value={payMethod} onChange={(e) => setPayMethod(e.target.value)} placeholder="Bank transfer, Cash, Check…" className="border-border" data-testid="payment-method-input" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs uppercase tracking-widest text-muted-foreground">Note</Label>
                      <Input value={payNote} onChange={(e) => setPayNote(e.target.value)} placeholder="Optional reference…" className="border-border" data-testid="payment-note-input" />
                    </div>
                  </div>

                  <DialogFooter>
                    <Button type="button" variant="ghost" onClick={() => setPayOpen(false)}>Cancel</Button>
                    <Button type="submit" data-testid="payment-submit-btn">Record payment</Button>
                  </DialogFooter>
                </>
              );
            })()}
          </form>
        </DialogContent>
      </Dialog>

      {/* Offset dialog — mark existing invoice as offset (non-cash settlement) */}
      <Dialog open={offsetOpen} onOpenChange={setOffsetOpen}>
        <DialogContent data-testid="offset-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black flex items-center gap-2">
              <GitBranch size={18} /> Mark as Offset — {offsetTarget?.invoice_no}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitOffset} className="space-y-4">
            <div className="p-3 border border-border rounded bg-muted/30 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-muted-foreground">Client</span><span className="font-medium">{offsetTarget?.client_name}</span></div>
              <div className="flex justify-between border-t border-border pt-1.5">
                <span className="text-muted-foreground">Invoice Total</span>
                <span className="tabular-nums font-heading font-bold">{fmt(offsetTarget?.total_amount, offsetTarget?.currency)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Current Balance</span>
                <span className="tabular-nums">{fmt(offsetTarget?.balance, offsetTarget?.currency)}</span>
              </div>
            </div>

            <div className="p-3 border-2 border-amber-200 rounded bg-amber-50/60 text-xs text-amber-900 leading-relaxed">
              <div className="font-bold uppercase tracking-widest text-[10px] mb-1">This action will</div>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Zero the balance and set status to <b>OFFSET</b></li>
                <li>Exclude the invoice from Outstanding, Aging &amp; Dashboard AR</li>
                <li>Keep the sale on the VAT / BIR reports</li>
                <li>Move it to the <b>Offsets</b> tab (reversible)</li>
              </ul>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Offset reason</Label>
              <Textarea
                rows={2}
                value={offsetReason}
                onChange={(e) => setOffsetReason(e.target.value)}
                placeholder="e.g. Netted against supplier payable BR-2026-0123 · Owner instruction dated 2026-02-14"
                className="border-border"
                data-testid="offset-reason-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOffsetOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="offset-submit-btn">
                <GitBranch size={14} className="mr-2" /> Confirm offset
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
